// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

// D-CRX-89 — one protocol slot on the drug record (Premedication, Anaphylaxis kit or Flushing): pick the
// drug's preset, create a new protocol, edit the assigned one (a new version for every drug using it), remove
// it from this drug, or retire it. The pick and "Remove from this drug" are saved with the drug like every
// other field; create / edit / retire are saved at once, audited, and this drug's slot follows them.

import { useState } from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import AppButton from '../../../components/ui/AppButton'
import ConfirmDialog from '../../../components/ui/ConfirmDialog'
import { useToast } from '../../../components/ui/Toast'
import SearchableSelect from '../../../components/common/SearchableSelect'
import type { AdminProtocol, AdminProtocolComponent } from '../../../api/client'
import { friendlyError } from '../../../api/errors'
import { SLOT_CATEGORY, type ProtocolSlot } from '../../../api/carerxProtocols'
import { useProtocolUsage, useRetireProtocol, useSlotImpact } from '../../../hooks/queries/carerxProtocols'
import ProtocolEditorDialog, { SLOT_LABEL } from './ProtocolEditorDialog'
import ProtocolImpact from './ProtocolImpact'

export default function DrugProtocolSlot({ slot, value, savedValue, protocols, components, drugReferenceId, onChange, children }: {
  slot: ProtocolSlot
  value: string | null
  /** The slot as saved on the drug (null = empty) — assigning to an empty slot never reaches enrolled patients. */
  savedValue?: string | null
  /** The pharmacy's current library (all categories). */
  protocols: AdminProtocol[]
  components: AdminProtocolComponent[]
  drugReferenceId: string | null
  onChange: (id: string | null) => void
  /** The assigned protocol's lines, rendered by the caller (premed/kit keep their per-drug added lines). */
  children?: React.ReactNode
}) {
  const toast = useToast()
  const retire = useRetireProtocol()
  const [editor, setEditor] = useState<'new' | 'edit' | null>(null)
  const [retiring, setRetiring] = useState(false)
  const usage = useProtocolUsage(retiring && value ? value : null)

  const inCategory = protocols.filter(p => p.category === SLOT_CATEGORY[slot])
  const options = inCategory.filter(p => p.active)
  const assigned = inCategory.find(p => p.id === value)
  const label = SLOT_LABEL[slot]
  const stale = value !== null && !assigned
  const drugNames = (usage.data?.drugs ?? []).map(d => d.name).join(', ')
  // Round-2 addendum (owner 2026-10-29): a protocol added to an EMPTY slot reaches new enrollments only.
  const emptyOnDrug = savedValue === null || savedValue === undefined
  const slotImpact = useSlotImpact(drugReferenceId, slot, emptyOnDrug)
  const without = slotImpact.data?.enrolled_without ?? 0

  const doRetire = () => {
    if (!value) return
    retire.mutate({ id: value, unassign: true }, {
      onSuccess: () => { toast.show(`${assigned?.name ?? 'Protocol'} retired.`, 'success'); setRetiring(false); onChange(null) },
      onError: e => toast.show(friendlyError(e, 'The protocol could not be retired.'), 'error'),
    })
  }

  return (
    <Box data-testid={`protocol-slot-${slot}`} sx={{ mb: 2 }}>
      <Box sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{label} protocol</Box>
      <SearchableSelect ariaLabel={`${label} protocol`} value={value ?? ''}
        onChange={v => onChange(v || null)}
        options={[{ value: '', label: '— none —' }, ...options.map(p => ({
          value: p.id,
          label: `${p.name} · v${p.version}${p.scope === 'global' ? ' · template' : ''}${p.lines.length === 0 ? ' (empty)' : ''}`,
        }))]} />
      {assigned && !assigned.active && (
        <Box sx={{ fontSize: 12, color: '#b23a48', mt: 0.5 }}>This protocol is retired. Pick another or remove it.</Box>
      )}
      {stale && (
        <Box sx={{ fontSize: 12, color: '#93610f', mt: 0.5 }}>
          The assigned protocol is not a current version in this pharmacy's library. Pick the current one.
        </Box>
      )}
      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
        <AppButton size="small" variant="outlined" onClick={() => setEditor('new')}>+ New protocol</AppButton>
        {assigned && <AppButton size="small" variant="outlined" onClick={() => setEditor('edit')}>Edit</AppButton>}
        {value && <AppButton size="small" onClick={() => onChange(null)}>Remove from this drug</AppButton>}
        {assigned?.active && <AppButton size="small" color="error" onClick={() => setRetiring(true)}>Retire</AppButton>}
      </Stack>
      {value && <Box sx={{ fontSize: 11.5, color: 'var(--color-text-soft)', mt: 0.5 }}>Picking or removing is saved with the drug.</Box>}
      {emptyOnDrug && without > 0 && (
        <Box data-testid={`slot-impact-${slot}`} sx={{ fontSize: 12, color: '#93610f', mt: 0.5 }}>
          {without} enrolled patient{without === 1 ? '' : 's'} keep{without === 1 ? 's' : ''} no {label.toLowerCase()} protocol;
          {' '}new enrollments get the one you assign here.
        </Box>
      )}
      {children}

      {editor && (
        <ProtocolEditorDialog open slot={slot} protocol={editor === 'edit' ? assigned ?? null : null}
          components={components} drugReferenceId={drugReferenceId}
          onClose={() => setEditor(null)}
          onSaved={s => { setEditor(null); onChange(s.id) }} />
      )}

      <ConfirmDialog open={retiring} tone="danger" busy={retire.isPending} maxWidth="sm"
        title={`Retire ${assigned?.name ?? 'this protocol'}?`} confirmLabel="Retire" busyLabel="Retiring…"
        confirmDisabled={usage.isLoading || usage.isError}
        error={usage.isError ? friendlyError(usage.error, 'Could not load the drugs that use this protocol.') : null}
        onCancel={() => setRetiring(false)} onConfirm={doRetire}
        body={
          <Stack spacing={1} data-testid="protocol-retire-warning">
            {usage.isLoading && <Box>Checking everything this protocol reaches…</Box>}
            {usage.data && (
              <>
                {usage.data.drugs.length > 0
                  ? <Box>Still used by <b>{drugNames}</b>. Retiring removes it from {usage.data.drugs.length === 1 ? 'that drug' : 'those drugs'}.</Box>
                  : <Box>No drug uses it.</Box>}
                <ProtocolImpact usage={usage.data} action="retire" />
              </>
            )}
            <Box>It is never deleted. If this is not acceptable, cancel and pick or create another protocol for the drug instead.</Box>
          </Stack>
        } />
    </Box>
  )
}
